import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import { Input } from "@/components/ui/input.jsx";
import {
  Download,
  Maximize2,
  Loader2,
  AlertTriangle,
  CalendarIcon,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.jsx";
import { Calendar } from "@/components/ui/calendar.jsx";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  useBanks,
  useBankTransactions,
  useBankCashFlow,
} from "@/lib/hooks/useBanks.js";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils.js";
import { Tooltip as TooltipComponent, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const LABEL_OPTIONS = [
  "ACHATS ET FRAIS GENERAUX",
  "BFR (EXPLOITATION)",
  "COMMISSIONS ET PENALITES",
  "CREDITS ET REMBOURSEMENTS",
  "ENTREES EXCEPTIONNELLES",
  "FINANCEMENT POSTE CLIENT",
  "FRAIS BANCAIRES",
  "IMPOTS",
  "LOCATION LONG TERME",
  "MOUVEMENTS FINANCIERS SPECIFIQUES",
  "REJETS ET IMPAYES",
  "RH",
  "SAISIES ADMINISTRATIVES",
  "TVA",
  "URSSAF",
];

// Labels that should only appear in positive entries
const POSITIVE_ONLY_LABELS = ["BFR (EXPLOITATION)", "ENTREES EXCEPTIONNELLES"];

// Function to normalize accented characters for matching
const normalizeLabel = (label) => {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .toUpperCase();
};

export function BankTransactions({ companyId }) {
  const [expandedAccounts, setExpandedAccounts] = useState([]);
  const [showClassification, setShowClassification] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState(null);
  const [descriptionFilter, setDescriptionFilter] = useState("");
  const [labelFilter, setLabelFilter] = useState("");
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [selectedCashFlowBankAccountId, setSelectedCashFlowBankAccountId] =
    useState(null);
  const [sortBy, setSortBy] = useState({ field: "date", order: "desc" });

  // Fetch banks and transactions
  const {
    banks,
    isLoading: banksLoading,
    error: banksError,
  } = useBanks(companyId);
  const {
    transactions: allTransactions,
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useBankTransactions(
    companyId,
    selectedBankAccountId,
    descriptionFilter,
  );

const cashFlowData = useMemo(() => {
  if (!banks || !selectedCashFlowBankAccountId) return [];
  // Find the bank matching the selected ID and get its cash_flow (statements)
  const selectedBank = banks.find(b => b.id === selectedCashFlowBankAccountId);
  return selectedBank?.cash_flow || [];
}, [banks, selectedCashFlowBankAccountId]);

// Add this to keep track of loading state via the banks hook
const cashFlowLoading = banksLoading;
  
  // Get unfiltered transactions for classified view (use cash flow bank filter)
  const { transactions: transactionsForClassification } = useBankTransactions(
    companyId,
    selectedCashFlowBankAccountId || null,
    "",
  );

  // Label color mapping for chart - separate colors for positive and negative
  // Using distinct, vibrant colors for better clarity and visual distinction
  // Positive colors: green, blue, and purple ranges only
  const labelColorsPositive = {
    "COMMISSIONS ET PENALITES": "#10B981", // Emerald green
    "REJETS ET IMPAYES": "#34D399", // Light emerald green
    "SAISIES ADMINISTRATIVES": "#059669", // Dark emerald green
    "CREDITS ET REMBOURSEMENTS": "#3B82F6", // Bright blue
    "FINANCEMENT POSTE CLIENT": "#2563EB", // Medium blue
    "LOCATION LONG TERME": "#1D4ED8", // Dark blue
    URSSAF: "#22C55E", // Bright green
    IMPOTS: "#16A34A", // Medium green
    TVA: "#15803D", // Dark green
    "BFR (EXPLOITATION)": "#8B5CF6", // Purple
    RH: "#6366F1", // Indigo (blue-purple)
    "MOUVEMENTS FINANCIERS SPECIFIQUES": "#A855F7", // Violet purple
    "ENTREES EXCEPTIONNELLES": "#10B981", // Emerald green
    "FRAIS BANCAIRES": "#64748B", // Slate gray
    "ACHATS ET FRAIS GENERAUX": "#F97316", // Orange (shouldn't appear in positive)
    AUTRES: "#94A3B8", // Light gray
  };

  const labelColorsNegative = {
    "COMMISSIONS ET PENALITES": "#DC2626", // Bright red
    "REJETS ET IMPAYES": "#EF4444", // Light red
    "SAISIES ADMINISTRATIVES": "#F59E0B", // Amber/Orange
    "CREDITS ET REMBOURSEMENTS": "#991B1B", // Dark red - indicates payment
    "FINANCEMENT POSTE CLIENT": "#854D0E", // Dark brown-orange - indicates expense
    "LOCATION LONG TERME": "#C2410C", // Rust orange
    URSSAF: "#F97316", // Orange
    IMPOTS: "#B91C1C", // Medium-dark red - indicates tax payment
    TVA: "#9F1239", // Burgundy
    "BFR (EXPLOITATION)": "#DC2626", // Red (shouldn't appear in negative)
    RH: "#BE185D", // Deep pink-red
    "MOUVEMENTS FINANCIERS SPECIFIQUES": "#581C87", // Dark purple
    "ENTREES EXCEPTIONNELLES": "#DC2626", // Red (shouldn't appear in negative)
    "FRAIS BANCAIRES": "#D97706", // Brown-orange
    "ACHATS ET FRAIS GENERAUX": "#EA580C", // Dark orange - indicates purchases/expenses
    AUTRES: "#374151", // Dark gray
  };

  // Filter transactions locally by label (case-insensitive, startsWith) and date range
  const transactions = useMemo(() => {
    let filtered = allTransactions;

    // Filter by label - use only the part before "|", accent-insensitive
    if (labelFilter) {
      filtered = filtered.filter((transaction) => {
        const baseLabel = (transaction.label || "").split("|")[0].trim();
        return normalizeLabel(baseLabel).startsWith(
          normalizeLabel(labelFilter),
        );
      });
    }

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.date);
        transactionDate.setHours(0, 0, 0, 0);

        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return transactionDate >= start && transactionDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return transactionDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return transactionDate <= end;
        }
        return true;
      });
    }

    return filtered;
  }, [allTransactions, labelFilter, startDate, endDate]);

  // Log "Autres" transactions when classification is enabled
  useEffect(() => {
    if (showClassification && transactionsForClassification.length > 0) {
      const autresTransactions = transactionsForClassification.filter(
        (transaction) => {
          const amount = parseFloat(transaction.amount);
          const rawLabel = transaction.label || "AUTRES";
          // Extract first part before "|" for matching
          const baseLabel = rawLabel.split("|")[0].trim();
          const normalizedBaseLabel = normalizeLabel(baseLabel);

          const matchedLabel =
            LABEL_OPTIONS.find((option) =>
              normalizedBaseLabel.startsWith(normalizeLabel(option)),
            ) || "AUTRES";

          // Case 1: No match in LABEL_OPTIONS
          if (matchedLabel === "AUTRES") return true;

          // Case 2: Negative amounts for labels that should only be positive
          if (amount < 0 && POSITIVE_ONLY_LABELS.includes(matchedLabel))
            return true;

          return false;
        },
      );
    }
  }, [showClassification, transactionsForClassification]);

  // Process transactions for classified cash flow chart
  const classifiedCashFlowData = useMemo(() => {
    if (!showClassification || !transactionsForClassification.length) return [];

    // Group transactions by month and label
    const monthlyData = {};

    transactionsForClassification.forEach((transaction) => {
      try {
        const date = new Date(transaction.date);
        if (isNaN(date.getTime())) {
          return;
        }

        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;
        const amount = parseFloat(transaction.amount);
        const rawLabel = transaction.label || "AUTRES";
        const label = rawLabel.split("|")[0].trim();

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {};
        }

        if (!monthlyData[monthKey][label]) {
          monthlyData[monthKey][label] = 0;
        }

        monthlyData[monthKey][label] += amount;
      } catch (error) {
        // Silently skip invalid transactions
      }
    });

    // Convert to array format and calculate running balance
    const sortedMonths = Object.keys(monthlyData).sort();

    if (sortedMonths.length === 0) {
      return [];
    }

    const result = [];

    sortedMonths.forEach((monthKey) => {
      const [year, month] = monthKey.split("-");
      const monthData = {
        month: `${year}-${month}-01`,
        monthLabel: new Date(
          parseInt(year),
          parseInt(month) - 1,
          1,
        ).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
      };

      // Initialize label categories - only positive-only labels for positive, exclude them from negative
      POSITIVE_ONLY_LABELS.forEach((label) => {
        monthData[`${label}_positive`] = 0;
        // Don't initialize negative for positive-only labels
      });

      // Initialize other labels for both positive and negative (excluding positive-only labels)
      LABEL_OPTIONS.filter(
        (label) => !POSITIVE_ONLY_LABELS.includes(label),
      ).forEach((label) => {
        monthData[`${label}_positive`] = 0;
        monthData[`${label}_negative`] = 0;
      });

      monthData["AUTRES_positive"] = 0;
      monthData["AUTRES_negative"] = 0;

      // Assign transactions to their labels (separate positive and negative)
      if (monthlyData[monthKey]) {
        Object.entries(monthlyData[monthKey]).forEach(([label, amount]) => {
          // Check if label matches any label option (case-insensitive, startsWith on first part, accent-insensitive)
          // Note: label is already extracted to first part before "|" at line 224
          const normalizedLabel = normalizeLabel(label);
          const matchedLabel =
            LABEL_OPTIONS.find((option) =>
              normalizedLabel.startsWith(normalizeLabel(option)),
            ) || "AUTRES";

          // Separate positive and negative amounts
          if (amount > 0) {
            // For positive amounts, use the matched label (including positive-only labels)
            monthData[`${matchedLabel}_positive`] += amount;
          } else if (amount < 0) {
            // For negative amounts, if it's a positive-only label, put it in AUTRES
            if (POSITIVE_ONLY_LABELS.includes(matchedLabel)) {
              monthData["AUTRES_negative"] += Math.abs(amount);
            } else {
              monthData[`${matchedLabel}_negative`] += Math.abs(amount);
            }
          }
        });
      }

      // Use API cash flow data for balance, entries, and outflows (match by month)
      // Find the statement matching the month
      const apiBalance = cashFlowData.find((cf) => {
        const apiDate = new Date(cf.date); // API uses 'date'
        return (
          apiDate.getFullYear() === parseInt(year) &&
          apiDate.getMonth() === parseInt(month) - 1
        );
      });

      if (apiBalance) {
        // Use correct Statement schema fields
        monthData["Solde de trésorerie"] = apiBalance.final_balance;
        monthData["total_income"] = Math.max(0, apiBalance.income || 0);
        monthData["total_expenses"] = Math.max(0, apiBalance.expense || 0);
        monthData["total_final_balance"] = apiBalance.final_balance;
      } else {
        // Fallback: calculate from transactions if API data not available
        const monthTotal = monthlyData[monthKey]
          ? Object.entries(monthlyData[monthKey]).reduce(
              (sum, [, val]) => sum + (typeof val === "number" ? val : 0),
              0,
            )
          : 0;
        const positiveTotal = monthlyData[monthKey]
          ? Object.entries(monthlyData[monthKey]).reduce(
              (sum, [, val]) => sum + (val > 0 ? val : 0),
              0,
            )
          : 0;
        const negativeTotal = monthlyData[monthKey]
          ? Object.entries(monthlyData[monthKey]).reduce(
              (sum, [, val]) => sum + (val < 0 ? Math.abs(val) : 0),
              0,
            )
          : 0;
        monthData["Solde de trésorerie"] = monthTotal; // Allow negative values
        monthData["total_income"] = positiveTotal;
        monthData["total_expenses"] = negativeTotal;
        monthData["total_final_balance"] = monthTotal;
      }

      result.push(monthData);
    });

    return result;
  }, [transactionsForClassification, showClassification, cashFlowData]);

  const toggleAccount = (accountId) => {
    setExpandedAccounts((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId],
    );
  };

  const formatAmount = (amount) => {
    const numericAmount = parseFloat(amount);
    const formatted = Math.abs(numericAmount).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return numericAmount >= 0 ? `+${formatted} €` : `-${formatted} €`;
  };

  // Format amount with spaces as thousand separators (French format)
  const formatAmountWithSpaces = (value) => {
    return value.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  };

  // Format integer amounts for Y-axis
  const formatAmountInteger = (value) => {
    return Math.round(value).toLocaleString("fr-FR", {
      useGrouping: true,
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Sort transactions helper
  const sortTransactions = (transactionsToSort) => {
    const sorted = [...transactionsToSort].sort((a, b) => {
      if (sortBy.field === "date") {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return sortBy.order === "asc" ? dateA - dateB : dateB - dateA;
      } else if (sortBy.field === "amount") {
        const valueA = parseFloat(a.amount) || 0;
        const valueB = parseFloat(b.amount) || 0;
        return sortBy.order === "asc" ? valueA - valueB : valueB - valueA;
      }
      return 0;
    });
    return sorted;
  };

  const handleSortChange = (field) => {
    setSortBy((prev) => {
      if (prev.field === field) {
        return { field, order: prev.order === "asc" ? "desc" : "asc" };
      }
      return { field, order: "desc" };
    });
  };

  const downloadTransactionsCSV = () => {
    if (!transactions.length) return;

    const headers = ["Date", "Description", "Catégorie", "Montant"];
    const csvContent = [
      headers.join(","),
      ...transactions.map((transaction) =>
        [
          transaction.date,
          `"${transaction.wording}"`,
          `"${transaction.label || ""}"`,
          transaction.amount,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "transactions_bancaires.csv";
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Bank Selection and Error Handling */}
      {banksError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Erreur lors du chargement des banques: {banksError.message}
          </AlertDescription>
        </Alert>
      )}

      {transactionsError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Erreur lors du chargement des transactions:{" "}
            {transactionsError.message}
          </AlertDescription>
        </Alert>
      )}
      
      {banksError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Erreur lors du chargement des comptes bancaires :{" "}
            {banksError.message}
          </AlertDescription>
        </Alert>
      )}
      <TransactionsTable
        transactions={transactions}
        transactionsLoading={transactionsLoading}
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        formatDate={formatDate}
        formatAmount={formatAmount}
        downloadTransactionsCSV={downloadTransactionsCSV}
        descriptionFilter={descriptionFilter}
        setDescriptionFilter={setDescriptionFilter}
        labelFilter={labelFilter}
        setLabelFilter={setLabelFilter}
        banks={banks}
        banksLoading={banksLoading}
        selectedBankAccountId={selectedBankAccountId}
        setSelectedBankAccountId={setSelectedBankAccountId}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        sortTransactions={sortTransactions}
      />

      <TreasuryGraph
        showClassification={showClassification}
        setShowClassification={setShowClassification}
        banks={banks}
        banksLoading={banksLoading}
        selectedCashFlowBankAccountId={selectedCashFlowBankAccountId}
        setSelectedCashFlowBankAccountId={setSelectedCashFlowBankAccountId}
        cashFlowData={cashFlowData}
        cashFlowLoading={cashFlowLoading}
        transactionsLoading={transactionsLoading}
        classifiedCashFlowData={classifiedCashFlowData}
        formatAmountInteger={formatAmountInteger}
        formatAmountWithSpaces={formatAmountWithSpaces}
      />
    </div>
  );
}

const DebouncedSearchInput = ({ initialValue, onDebouncedChange }) => {
  const [localValue, setLocalValue] = useState(initialValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      onDebouncedChange(localValue);
    }, 500); // delay in ms to trigger the API/Parent state
    return () => clearTimeout(handler);
  }, [localValue, onDebouncedChange]);

  return (
    <Input
      placeholder="Filtrer par description..."
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      className="flex-1 max-w-md"
    />
  );
};

function TransactionsTable({
  transactions,
  transactionsLoading,
  sortBy,
  handleSortChange,
  formatDate,
  formatAmount,
  downloadTransactionsCSV,
  descriptionFilter,
  setDescriptionFilter,
  labelFilter,
  setLabelFilter,
  banks,
  banksLoading,
  selectedBankAccountId,
  setSelectedBankAccountId,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  sortTransactions
}) {

  const renderedRows = useMemo(()=>{

    return sortTransactions(transactions).map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="font-medium">
                      {formatDate(transaction.date)}
                    </TableCell>
                    <TableCell className="w-[500px]">
                      <TooltipProvider>
                        <TooltipComponent>
                          <TooltipTrigger asChild>
                            <div className="text-sm line-clamp-2 cursor-help">
                              {transaction.wording || "-"}
                            </div>
                          </TooltipTrigger>
                          {transaction.wording && (
                            <TooltipContent>
                              <p className="max-w-xs">
                                {transaction.wording}
                              </p>
                            </TooltipContent>
                          )}
                        </TooltipComponent>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>{transaction.label || "-"}</TableCell>
                    <TableCell
                      className={`text-right font-medium ${
                        parseFloat(transaction.amount) >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatAmount(transaction.amount)}
                    </TableCell>
                  </TableRow>
                ))
  },[transactions, sortBy])
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Transactions bancaires</CardTitle>
          <div className="flex items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!transactions.length}
                >
                  <Maximize2 className="h-4 w-4 mr-2" />
                  Agrandir
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    Transactions bancaires - Vue agrandie
                  </DialogTitle>
                </DialogHeader>
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-medium text-lg">
                          <div
                            className="flex items-center gap-1 cursor-pointer hover:text-foreground select-none"
                            onClick={() => handleSortChange("date")}
                          >
                            Date
                            {sortBy.field === "date" ? (
                              sortBy.order === "asc" ? (
                                <ArrowUp className="h-3 w-3" />
                              ) : (
                                <ArrowDown className="h-3 w-3" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-50" />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="font-medium text-lg">
                          Description
                        </TableHead>
                        <TableHead className="font-medium text-lg">
                          Catégorie
                        </TableHead>
                        <TableHead className="text-right font-medium text-lg">
                          <div
                            className="flex items-center justify-end gap-1 cursor-pointer hover:text-foreground select-none"
                            onClick={() => handleSortChange("amount")}
                          >
                            Montant
                            {sortBy.field === "amount" ? (
                              sortBy.order === "asc" ? (
                                <ArrowUp className="h-3 w-3" />
                              ) : (
                                <ArrowDown className="h-3 w-3" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-50" />
                            )}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortTransactions(transactions).map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell className="font-medium text-base">
                            {formatDate(transaction.date)}
                          </TableCell>
                          <TableCell className="text-base">
                            {transaction.wording}
                          </TableCell>
                          <TableCell className="text-base">
                            {transaction.label || "-"}
                          </TableCell>
                          <TableCell
                            className={`text-right font-medium text-base ${
                              parseFloat(transaction.amount) >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {formatAmount(transaction.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              onClick={downloadTransactionsCSV}
              variant="outline"
              size="sm"
              disabled={!transactions.length}
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger CSV
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="mt-4 space-y-4">
          {/* Search input on top */}
          <div className="flex items-center gap-3">
            <label
              htmlFor="description-search"
              className="text-sm font-medium min-w-[100px]"
            >
              Rechercher:
            </label>
           <DebouncedSearchInput 
    initialValue={descriptionFilter} 
    onDebouncedChange={setDescriptionFilter}
  />
          </div>

          {/* Other filters - Grid layout for better symmetry */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Bank Filter */}
            <div className="flex flex-col gap-2">
              <label htmlFor="bank-select" className="text-sm font-medium">
                Banque
              </label>
              <div className="flex items-center gap-2">
                <Select
                  value={selectedBankAccountId?.toString() || "all"}
                  onValueChange={(value) =>
                    setSelectedBankAccountId(
                      value === "all" ? null : parseInt(value),
                    )
                  }
                  disabled={banksLoading}
                >
                  <SelectTrigger id="bank-select" className="w-full">
                    <SelectValue
                      placeholder={
                        banksLoading ? "Chargement..." : "Toutes les banques"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les banques</SelectItem>
                    {banks.map((bank) => (
                      <SelectItem key={bank.id} value={bank.id.toString()}>
                        {bank.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {banksLoading && (
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                )}
              </div>
            </div>

            {/* Label Filter */}
            <div className="flex flex-col gap-2">
              <label htmlFor="label-select" className="text-sm font-medium">
                Catégorie
              </label>
              <Select
                value={labelFilter || "all"}
                onValueChange={(value) =>
                  setLabelFilter(value === "all" ? "" : value)
                }
              >
                <SelectTrigger id="label-select" className="w-full">
                  <SelectValue placeholder="Toutes les catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {LABEL_OPTIONS.map((label) => (
                    <SelectItem key={label} value={label}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Date début</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {startDate
                        ? format(startDate, "PPP", { locale: fr })
                        : "Sélectionner"}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Date fin</label>
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {endDate
                          ? format(endDate, "PPP", { locale: fr })
                          : "Sélectionner"}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {(startDate || endDate) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => {
                      setStartDate(undefined);
                      setEndDate(undefined);
                    }}
                    title="Réinitialiser dates"
                  >
                    <span className="sr-only">Réinitialiser dates</span>×
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="h-[400px] overflow-auto">
          {transactionsLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Chargement des transactions...
                </p>
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-muted-foreground">
                  Aucune transaction trouvée
                </p>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium">
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:text-foreground select-none"
                      onClick={() => handleSortChange("date")}
                    >
                      Date
                      {sortBy.field === "date" ? (
                        sortBy.order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="font-medium w-[800px]">
                    Description
                  </TableHead>
                  <TableHead className="font-medium">Catégorie</TableHead>
                  <TableHead className="text-right font-medium">
                    <div
                      className="flex items-center justify-end gap-1 cursor-pointer hover:text-foreground select-none"
                      onClick={() => handleSortChange("amount")}
                    >
                      Montant
                      {sortBy.field === "amount" ? (
                        sortBy.order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderedRows}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TreasuryGraph({
  showClassification,
  setShowClassification,
  banks,
  banksLoading,
  selectedCashFlowBankAccountId,
  setSelectedCashFlowBankAccountId,
  cashFlowData,
  cashFlowLoading,
  transactionsLoading,
  classifiedCashFlowData,
  formatAmountInteger,
  formatAmountWithSpaces,
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              {showClassification
                ? "Courbe de trésorerie"
                : "Évolution des soldes bancaires"}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {showClassification
                ? "Évolution des entrées et sorties sur les 6 derniers mois"
                : "Encaissements et décaissements par mois"}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="classification"
              checked={showClassification}
              onCheckedChange={(checked) =>
                setShowClassification(checked === true)
              }
            />
            <label htmlFor="classification" className="text-sm font-medium">
              Classification des transactions
            </label>
          </div>
        </div>
      </CardHeader>

      {/* Cash Flow Bank Filter */}
      <div className="px-6 pb-4">
        <div className="flex items-center gap-4">
          <label htmlFor="cashflow-bank-select" className="text-sm font-medium">
            Banque pour le graphique:
          </label>
          <Select
            value={selectedCashFlowBankAccountId?.toString() || "all"}
            onValueChange={(value) =>
              setSelectedCashFlowBankAccountId(
                value === "all" ? null : parseInt(value),
              )
            }
            disabled={banksLoading}
          >
            <SelectTrigger className="w-[300px]" id="cashflow-bank-select">
              <SelectValue
                placeholder={
                  banksLoading ? "Chargement..." : "Filtrer par banque"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les banques</SelectItem>
              {banks.map((bank) => (
                <SelectItem key={bank.id} value={bank.id.toString()}>
                  {bank.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {banksLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
      </div>

      <CardContent>
        {showClassification ? (
          // Classified view with stacked bars
          transactionsLoading ? (
            <div className="flex items-center justify-center h-[450px]">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Chargement des transactions...
                </p>
              </div>
            </div>
          ) : classifiedCashFlowData.length === 0 ? (
            <div className="flex items-center justify-center h-[450px]">
              <div className="text-center">
                <p className="text-muted-foreground">
                  Aucune donnée de trésorerie disponible
                </p>
              </div>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart
                  data={classifiedCashFlowData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  {/* --- MODIFICATION START --- */}
                  {/* Changed domain to 'auto', 'auto' and updated tickFormatter to allow negative values */}
                  <YAxis
                    tickFormatter={(value) => {
                      return `${formatAmountInteger(value)}€`;
                    }}
                    domain={["auto", "auto"]}
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    allowDataOverflow={false}
                  />
                  {/* Removed the second YAxis (yAxisId="right") */}
                  {/* --- MODIFICATION END --- */}
                  <ReferenceLine y={0} stroke="#71717A" strokeWidth={2} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;

                      // Filter out zero values and separate positive and negative
                      const filteredPayload = payload.filter((item) => {
                        const value =
                          typeof item.value === "number" ? item.value : 0;
                        return (
                          value !== 0 && value !== null && value !== undefined
                        );
                      });

                      if (filteredPayload.length === 0) return null;

                      const date = payload[0]?.payload?.month
                        ? new Date(payload[0].payload.month)
                        : null;

                      // Get cash flow data from payload
                      const dataPayload = payload[0]?.payload;
                      const totalIncome = dataPayload?.total_income || 0;
                      const totalExpenses = dataPayload?.total_expenses || 0;
                      const cashBalance = dataPayload?.total_final_balance || 0;

                      // Separate positive and negative entries
                      const positiveEntries = filteredPayload.filter(
                        (item) =>
                          typeof item.name === "string" &&
                          item.name.includes("(Entrées)"),
                      );
                      const negativeEntries = filteredPayload.filter(
                        (item) =>
                          typeof item.name === "string" &&
                          item.name.includes("(Sorties)"),
                      );

                      // Calculate totals
                      const totalPositive = positiveEntries.reduce(
                        (sum, entry) =>
                          sum +
                          (typeof entry.value === "number" ? entry.value : 0),
                        0,
                      );
                      const totalNegative = negativeEntries.reduce(
                        (sum, entry) =>
                          sum +
                          (typeof entry.value === "number"
                            ? Math.abs(entry.value)
                            : 0),
                        0,
                      );

                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-md min-w-[250px]">
                          {date && (
                            <div className="mb-3 font-semibold text-base border-b pb-2">
                              {date.toLocaleDateString("fr-FR", {
                                year: "numeric",
                                month: "long",
                              })}
                            </div>
                          )}
                          {positiveEntries.length > 0 && (
                            <div className="mb-3">
                              <div className="text-xs font-semibold mb-2 text-green-700">
                                Entrées
                              </div>
                              <div className="space-y-1">
                                {positiveEntries.map((entry, index) => {
                                  const labelName =
                                    typeof entry.name === "string"
                                      ? entry.name.replace(" (Entrées)", "")
                                      : entry.name;
                                  return (
                                    <div
                                      key={index}
                                      className="flex items-center gap-2"
                                    >
                                      <div
                                        className="h-3 w-3 rounded shrink-0"
                                        style={{
                                          backgroundColor: entry.color,
                                        }}
                                      />
                                      <span className="text-sm">
                                        {labelName}:{" "}
                                        {typeof entry.value === "number"
                                          ? formatAmountWithSpaces(entry.value)
                                          : entry.value}
                                        €
                                      </span>
                                    </div>
                                  );
                                })}
                                {totalPositive > 0 && (
                                  <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                                    <div className="h-3 w-3 rounded shrink-0 bg-green-700" />
                                    <span className="text-sm font-semibold">
                                      Total Entrées:{" "}
                                      {formatAmountWithSpaces(totalPositive)}€
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {negativeEntries.length > 0 && (
                            <div className="mb-3">
                              <div className="text-xs font-semibold mb-2 text-red-700">
                                Sorties
                              </div>
                              <div className="space-y-1">
                                {negativeEntries.map((entry, index) => {
                                  const labelName =
                                    typeof entry.name === "string"
                                      ? entry.name.replace(" (Sorties)", "")
                                      : entry.name;
                                  const displayValue =
                                    typeof entry.value === "number"
                                      ? formatAmountWithSpaces(
                                          Math.abs(entry.value),
                                        )
                                      : entry.value;
                                  return (
                                    <div
                                      key={index}
                                      className="flex items-center gap-2"
                                    >
                                      <div
                                        className="h-3 w-3 rounded shrink-0"
                                        style={{
                                          backgroundColor: entry.color,
                                        }}
                                      />
                                      <span className="text-sm">
                                        {labelName}: {displayValue}€
                                      </span>
                                    </div>
                                  );
                                })}
                                {totalNegative > 0 && (
                                  <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                                    <div className="h-3 w-3 rounded shrink-0 bg-red-700" />
                                    <span className="text-sm font-semibold">
                                      Total Sorties:{" "}
                                      {formatAmountWithSpaces(totalNegative)}€
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Cash Flow Information */}
                          <div className="mt-3 pt-3 border-t">
                            <div className="text-xs font-semibold mb-2 text-gray-700">
                              Trésorerie
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>Encaissements:</span>
                                <span className="font-medium text-green-700">
                                  {formatAmountWithSpaces(totalIncome)}€
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Décaissements:</span>
                                <span className="font-medium text-red-700">
                                  {formatAmountWithSpaces(totalExpenses)}€
                                </span>
                              </div>
                              <div className="flex justify-between pt-1 border-t">
                                <span className="font-semibold">Solde:</span>
                                <span className="font-semibold">
                                  {formatAmountWithSpaces(cashBalance)}€
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend content={() => null} />

                  {/* Positive stacked bars - only 3 labels */}
                  {POSITIVE_ONLY_LABELS.map((label) => (
                    <Bar
                      key={`${label}_positive`}
                      dataKey={`${label}_positive`}
                      stackId="positive"
                      fill={labelColorsPositive[label] || "#10B981"}
                      name={`${label} (Entrées)`}
                    />
                  ))}
                  <Bar
                    dataKey="AUTRES_positive"
                    stackId="positive"
                    fill={labelColorsPositive["AUTRES"]}
                    name="AUTRES (Entrées)"
                  />

                  {/* Negative stacked bars - exclude positive-only labels */}
                  {LABEL_OPTIONS.filter(
                    (label) => !POSITIVE_ONLY_LABELS.includes(label),
                  ).map((label) => (
                    <Bar
                      key={`${label}_negative`}
                      dataKey={`${label}_negative`}
                      stackId="negative"
                      fill={labelColorsNegative[label] || "#DC2626"}
                      name={`${label} (Sorties)`}
                    />
                  ))}
                  <Bar
                    dataKey="AUTRES_negative"
                    stackId="negative"
                    fill={labelColorsNegative["AUTRES"]}
                    name="AUTRES (Sorties)"
                  />

                  {/* --- MODIFICATION START --- */}
                  {/* Line chart showing cash balance - removed yAxisId="right" */}
                  <Line
                    type="monotone"
                    dataKey="total_final_balance"
                    stroke="#71717A"
                    strokeWidth={2}
                    name="Solde de trésorerie"
                    dot={{ fill: "#71717A", strokeWidth: 2, r: 4 }}
                    connectNulls={false}
                  />
                  {/* --- MODIFICATION END --- */}
                </ComposedChart>
              </ResponsiveContainer>

              {/* Custom Legend Below Chart */}
              <div className="mt-4 px-4">
                <div className="space-y-4">
                  {/* Encaissements Section */}
                  <div>
                    <div className="text-sm font-semibold mb-2 text-green-700">
                      Encaissements
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 justify-items-start">
                      {POSITIVE_ONLY_LABELS.map((label) => (
                        <div
                          key={`${label}_pos`}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="h-3 w-3 rounded shrink-0"
                            style={{
                              backgroundColor:
                                labelColorsPositive[label] || "#10B981",
                            }}
                          />
                          <span className="text-xs">{label}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded shrink-0"
                          style={{
                            backgroundColor: labelColorsPositive["AUTRES"],
                          }}
                        />
                        <span className="text-xs">AUTRES</span>
                      </div>
                    </div>
                  </div>

                  {/* Décaissements Section */}
                  <div>
                    <div className="text-sm font-semibold mb-2 text-red-700">
                      Décaissements
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 justify-items-start">
                      {LABEL_OPTIONS.filter(
                        (label) => !POSITIVE_ONLY_LABELS.includes(label),
                      ).map((label) => (
                        <div
                          key={`${label}_neg`}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="h-3 w-3 rounded shrink-0"
                            style={{
                              backgroundColor:
                                labelColorsNegative[label] || "#DC2626",
                            }}
                          />
                          <span className="text-xs">{label}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded shrink-0"
                          style={{
                            backgroundColor: labelColorsNegative["AUTRES"],
                          }}
                        />
                        <span className="text-xs">AUTRES</span>
                      </div>
                    </div>
                  </div>

                  {/* Cash Flow Line */}
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 shrink-0 flex items-center justify-center">
                        <div className="h-0.5 w-3 bg-black" />
                      </div>
                      <span className="text-xs">Solde de trésorerie</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        ) : // Simple view (original)
        cashFlowLoading ? (
          <div className="flex items-center justify-center h-[350px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">
                Chargement des données de trésorerie...
              </p>
            </div>
          </div>
        ) : cashFlowData.length === 0 ? (
          <div className="flex items-center justify-center h-[350px]">
            <div className="text-center">
              <p className="text-muted-foreground">
                Aucune donnée de trésorerie disponible
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart
              data={cashFlowData}
              margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis
                tickFormatter={(value) => {
                  return `${formatAmountInteger(value)}€`;
                }}
                domain={["auto", "auto"]}
                allowDecimals={false}
                tick={{ fontSize: 12 }}
              />
              <ReferenceLine y={0} stroke="#71717A" strokeWidth={2} />
              <Tooltip
                formatter={(value, name) => {
                  const fieldNames = {
                    total_income: "Encaissements",
                    total_expenses: "Décaissements",
                  };
                  const formattedValue =
                    typeof value === "number"
                      ? formatAmountWithSpaces(value)
                      : value;
                  return [`${formattedValue}€`, fieldNames[name] || name];
                }}
                labelFormatter={(value) => {
                  return value;
                }}
              />
              <Legend
                formatter={(value) => {
                  const fieldNames = {
                    total_income: "Encaissements",
                    total_expenses: "Décaissements",
                  };
                  return fieldNames[value] || value;
                }}
              />

              {/* Bar chart for income (green) */}
              <Bar
                dataKey="total_income"
                fill="#10B981"
                name="total_income"
                radius={[2, 2, 0, 0]}
              />

              {/* Bar chart for expenses (red) */}
              <Bar
                dataKey="total_expenses"
                fill="#EF4444"
                name="total_expenses"
                radius={[2, 2, 0, 0]}
              />

              {/* --- MODIFICATION START --- */}
              {/* Line chart showing balance evolution - removed yAxisId="right" */}
              <Line
                type="monotone"
                dataKey="total_final_balance"
                stroke="#71717A"
                strokeWidth={2}
                name="evolution"
                dot={{ fill: "#71717A", strokeWidth: 2, r: 4 }}
                connectNulls={false}
              />
              {/* --- MODIFICATION END --- */}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
